<template>
  <div
    class="social-marketing-image"
    :class="{
      'social-marketing-image--no-image': !image,
    }"
    :style="imageStyle"
    ref="socialMarketingImage"
  >
    <div
      class="image-dragger"
      :class="{ 'image-dragger--edit-mode': editMode }"
      :style="filterStyle"
      @mousedown="onMousedown"
      @mousemove="onMouseMove"
    ></div>
    <slot />
  </div>
</template>

<script>
import Vue from 'vue';

const imagesPromises = {};

export default Vue.extend({
  name: 'SocialMarketingImage',
  props: {
    userImage: {
      type: Object,
      required: false,
    },
    gradient: {
      type: String,
      required: false,
    },
    filterColor: {
      type: String,
      required: false,
    },
    zoom: {
      type: Number,
      required: false,
      default: 3,
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false,
      isResizing: false,
      originX: undefined,
      originY: undefined,
      backgroundPositionTop: 0,
      backgroundPositionLeft: 0,
      backgroundSize: 100,
      minBackgroundSize: 100,
      imageSizes: undefined,
      resizeObserver: undefined,
      width: 0,
      height: 0,
    };
  },
  computed: {
    image() {
      if (typeof this.userImage === 'string') {
        return this.userImage;
      }
      return this.userImage?.storage_url || this.userImage?.embedded_image_url;
    },
    imageStyle() {
      if (!this.image || !this.imageSizes) {
        return {};
      }

      const backgroundImages = [this.gradient, `url(${this.image})`]
        .filter(Boolean)
        .join(', ');

      const backgroundPosition = [
        this.gradient && 'center',
        `${this.backgroundPositionLeft}px ${this.backgroundPositionTop}px`,
      ]
        .filter(Boolean)
        .join(', ');

      const backgroundSize = [
        this.gradient && '100% 100%',
        `${(this.imageSizes.width * this.backgroundSize) / 100}px`,
      ]
        .filter(Boolean)
        .join(', ');

      return {
        backgroundImage: backgroundImages,
        backgroundPosition: backgroundPosition,
        backgroundSize: backgroundSize,
      };
    },
    filterStyle() {
      return {
        backgroundColor: this.filterColor,
      };
    },
    maxLeftMovement() {
      if (!this.imageSizes) {
        return 0;
      }

      return (this.imageSizes.width * this.backgroundSize) / 100 - this.width;
    },
    maxTopMovement() {
      if (!this.imageSizes) {
        return 0;
      }

      return (this.imageSizes.height * this.backgroundSize) / 100 - this.height;
    },
  },
  methods: {
    onMousedown(event) {
      if (!this.editMode) {
        return;
      }
      // if comand key is pressed, don't drag
      if (event.shiftKey) {
        this.isDragging = false;
        this.isResizing = true;
      } else {
        this.isDragging = true;
        this.isResizing = false;
      }

      this.originX = event.clientX;
      this.originY = event.clientY;
    },
    onMouseup() {
      this.isDragging = false;
      this.isResizing = false;
    },
    onMouseMove(event) {
      if (!this.isDragging && !this.isResizing) {
        return;
      }

      const { clientX, clientY } = event;
      const x = (clientX - this.originX) * this.zoom;
      const y = (clientY - this.originY) * this.zoom;
      this.originX = clientX;
      this.originY = clientY;

      if (this.isDragging) {
        const newLeft = this.backgroundPositionLeft + x;
        const newTop = this.backgroundPositionTop + y;

        this.backgroundPositionLeft = Math.max(
          Math.min(newLeft, 0),
          -this.maxLeftMovement
        );
        this.backgroundPositionTop = Math.max(
          Math.min(newTop, 0),
          -this.maxTopMovement
        );
      } else if (this.isResizing) {
        // let move = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));
        // if (clientY < this.originX) {
        // }

        const newBackgroundSize = this.backgroundSize + y;
        this.backgroundSize = Math.max(
          newBackgroundSize,
          this.minBackgroundSize
        );

        const { width, height } = this.imageSizes;
        const newLeft = this.backgroundPositionLeft - (width / 100) * (y / 2);
        const newTop = this.backgroundPositionTop - (height / 100) * (y / 2);

        this.backgroundPositionLeft = Math.max(
          Math.min(newLeft, 0),
          -this.maxLeftMovement
        );

        this.backgroundPositionTop = Math.max(
          Math.min(newTop, 0),
          -this.maxTopMovement
        );
      }
    },
    setupImage() {
      if (!this.image) {
        return;
      }
      imagesPromises[this.image].then(({ width, height }) => {
        this.imageSizes = { width, height };

        // calculate image size in percentage to fit the container
        const widthPercentage = this.width / width;
        const heightPercentage = this.height / height;

        this.backgroundSize = Math.max(widthPercentage, heightPercentage) * 100;
        this.minBackgroundSize = this.backgroundSize;

        // center image base on background size
        this.backgroundPositionLeft =
          (this.width - (width * this.backgroundSize) / 100) / 2;
        this.backgroundPositionTop =
          (this.height - (height * this.backgroundSize) / 100) / 2;
      });
    },
    loadImage() {
      if (this.image && !imagesPromises[this.image]) {
        const img = new Image();

        const imagePromise = new Promise((resolve, reject) => {
          img.onload = () => {
            const { width, height } = img;
            resolve({ width, height });
          };
          img.onerror = reject;
          img.src = this.image;
        });

        imagesPromises[this.image] = imagePromise;
      }
    },
  },
  mounted() {
    document.addEventListener('mouseup', this.onMouseup);

    this.resizeObserver = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;

      const firstSetup = !this.width;
      this.width = width;
      this.height = height;

      if (firstSetup) {
        this.setupImage();
      }
    });

    this.resizeObserver.observe(this.$refs.socialMarketingImage);
  },
  beforeDestroy() {
    document.removeEventListener('mouseup', this.onMouseup);
    this.resizeObserver.disconnect();
  },
  watch: {
    image: {
      handler() {
        this.loadImage();
        const firstSetup = !this.width;
        if (!firstSetup) {
          this.setupImage();
        }
      },
      immediate: true,
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.social-marketing-image {
  position: relative;
  background-repeat: no-repeat;
  background-size: cover;

  display: flex;
  flex-direction: column;
  height: 100%;
  &--no-image {
    background-color: $medium-gray;
  }
}

.image-dragger {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  &--edit-mode {
    cursor: all-scroll;
  }
}
</style>
